import {
    Component,
    ChangeDetectionStrategy,
    Input,
    ChangeDetectorRef,
    Output,
    EventEmitter,
    SimpleChanges,
    AfterViewInit,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GetEvsesForChargingStationResponse, Evse } from '@kempoweroyj/ngx-evse-api-client';
import { ReportingApiService } from '@kempoweroyj/ngx-reporting-api-client';
import { ReportingQueryService } from '../../../../services/reporting-query.service';
import { Transaction, TransactionsApiService } from '@kempoweroyj/ngx-transactions-api-client';
import { combineLatest, Subject } from 'rxjs';
import { auditTime, distinctUntilChanged, exhaustMap, filter, map, switchMap, take } from 'rxjs/operators';
import { DialogLinkService } from 'shared/routed-dialog/dialog-link.service';
import { calculateDurationAndZoomLevel, TimelineZoomLevel } from '../timeline-controls/timeline-controls.component';
import { GetEvseStatusHistoryResponse, StatusApiService } from 'api/status.api';
import { ConnectionsApiService, GetConnectionsResponse } from 'api/connections.api';
import { NotificationsService } from 'api/notifications.api';
import * as Highcharts from 'highcharts';
import { StationsService } from 'shared/stations-service/stations.service';
import { maxBy } from 'lodash';
import { retryWithDelay } from 'shared/utils/retryWithDelay';
import { StationCapabilitiesService } from 'shared/stations-service/station-capabilities.service';

/**
 * Highlight a point
 */
(Highcharts.Point.prototype as any).highlight = function (event) {
    event = this.series.chart.pointer.normalize(event);

    // Show the hover marker
    this.onMouseOver();

    // Show the crosshair
    this.series.chart.xAxis[0].drawCrosshair(event, this);
};

/**
 * Override the reset function, we don't need to hide the tooltips and crosshairs.
 */
Highcharts.Pointer.prototype.reset = function () {
    return undefined;
};

@Component({
    selector: 'ce-timeline',
    templateUrl: './timeline.component.html',
    styleUrls: ['./timeline.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
@UntilDestroy()
export class TimelineComponent implements AfterViewInit {
    @Input() stationId: string;
    @Input() locationUid: string;
    @Input() connectors: GetEvsesForChargingStationResponse;
    @Input() startTime: string;
    @Output() startTimeChange = new EventEmitter<string>();
    @Input() endTime: string;
    @Output() endTimeChange = new EventEmitter<string>();
    @Input() zoomLevel: TimelineZoomLevel;
    @Output() zoomLevelChange = new EventEmitter<TimelineZoomLevel>();
    @Input() seekTime: string;
    @Output() seekTimeChange = new EventEmitter<string>();

    txs: Transaction[];

    private requestUpdate$ = new Subject<void>();
    private readonly onDestroy$ = new Subject<void>();

    totalPowerSamples: [number, number, number][] = [];
    totalPowerSamplesLocation: [number, number, number][] = [];
    reservationSamples: [number, number, number][] = [];

    connectorStatusHistory: GetEvseStatusHistoryResponse[];

    connections: GetConnectionsResponse;

    /** Timestamps when charger has rebooted */
    bootTimes: string[];

    constructor(
        private cdRef: ChangeDetectorRef,
        private txApi: TransactionsApiService,
        private reportingApi: ReportingApiService,
        private reportingQuery: ReportingQueryService,
        private dialogLink: DialogLinkService,
        private statusApi: StatusApiService,
        private notifications: NotificationsService,
        private connectionsApi: ConnectionsApiService,
        private stations: StationsService,
        private stationCapabilitiesService: StationCapabilitiesService
    ) {
        const powerPerPmc$ = () =>
            this.stations.station$(this.stationId).pipe(
                map((res) => this.stations.getActivePowerModules(res)),
                map((devices) => maxBy(devices, (d) => d.details?.powerKw)?.details?.powerKw || 20),
                distinctUntilChanged()
            );
        const isDynamic$ = () =>
            this.stations.station$(this.stationId).pipe(
                map((res) => !!this.stations.getDynamicsType(res)),
                distinctUntilChanged()
            );

        // transactions
        this.requestUpdate$
            .pipe(
                filter(() => !!this.stationId),
                exhaustMap(() =>
                    this.txApi.getTransactions(this.stationId, {
                        startDate: this.startTime,
                        endDate: this.endTime,
                        returnAll: true,
                    })
                ),
                retryWithDelay(5000),
                untilDestroyed(this)
            )
            .subscribe((res) => {
                this.txs = res.transactions;
                this.cdRef.markForCheck();
            });

        // location power graph, if requested
        this.requestUpdate$
            .pipe(
                filter(() => !!this.locationUid),
                exhaustMap(() => this.fetchLocationPowerGraph()),
                retryWithDelay(5000),
                untilDestroyed(this)
            )
            .subscribe((totalPowerSamplesLocation) => {
                this.totalPowerSamplesLocation = totalPowerSamplesLocation as any;
                this.cdRef.markForCheck();
            });

        this.requestUpdate$
            .pipe(
                filter(() => !!this.stationId),
                switchMap(() => combineLatest([powerPerPmc$(), isDynamic$()])),
                exhaustMap(async ([powerPerPmc, isDynamic]) =>
                    !(await stationCapabilitiesService.isCapableOf(this.stationId, 'HardwareKempower'))
                        ? this.fetchStationPowerGraph()
                        : this.fetchPowerGraph(powerPerPmc, isDynamic)
                ),
                retryWithDelay(5000),
                untilDestroyed(this)
            )
            .subscribe(async (samples) => {
                if (!(await stationCapabilitiesService.isCapableOf(this.stationId, 'HardwareKempower'))) {
                    this.totalPowerSamples = samples.totalPowerSamples as [number, number, number][];
                    this.cdRef.markForCheck();
                } else {
                    if (samples.totalPowerSamples.every((s) => s[1] === null)) {
                        this.totalPowerSamples = null;
                    } else {
                        this.totalPowerSamples = samples.totalPowerSamples.map((s) => [s[0], s[1], 0 || 0]);
                    }
                    if (!samples.reservationSamples || samples.reservationSamples.every((s) => s[1] === null)) {
                        this.reservationSamples = null;
                    } else {
                        this.reservationSamples = samples.reservationSamples.map((s) => [s[0], s[1], 0 || 0]);
                    }
                    this.cdRef.markForCheck();
                }
            });

        // Connector statusHistory
        this.requestUpdate$
            .pipe(
                filter(() => !!this.stationId),
                exhaustMap(() => this.fetchConnectorStatusHistory()),
                retryWithDelay(5000),
                untilDestroyed(this)
            )
            .subscribe((history) => {
                this.connectorStatusHistory = history;
                this.cdRef.markForCheck();
            });

        // ConnectionStatus
        this.requestUpdate$
            .pipe(
                filter(() => !!this.stationId),
                exhaustMap(() => this.fetchConnectionStatus()),
                retryWithDelay(5000),
                untilDestroyed(this)
            )
            .subscribe((connections) => {
                this.connections = connections;
            });

        // Charger boot times
        this.requestUpdate$
            .pipe(
                filter(() => !!this.stationId),
                exhaustMap(() => this.fetchBootTimes()),
                retryWithDelay(5000),
                untilDestroyed(this)
            )
            .subscribe((bootTimes) => {
                this.bootTimes = bootTimes;
            });

        this.notifications.notification$
            .pipe(
                filter((n) => n.type === 'ChargingStationUpdated' && n.changedSchemas?.includes('tx')),
                auditTime(20000), // do not update too frequently
                untilDestroyed(this)
            )
            .subscribe(() => {
                this.requestUpdate$.next();
            });
    }

    ngAfterViewInit(): void {
        ['mousemove', 'touchmove', 'touchstart'].forEach(function (eventType) {
            document.getElementById('timeline').addEventListener(eventType, function (e) {
                let chart;

                for (let i = 0; i < Highcharts.charts.length; i++) {
                    chart = Highcharts.charts[i];

                    // Find coordinates within the chart
                    if (chart && 'pointer' in chart) {
                        const event = chart.pointer?.normalize(e);
                        // Get the hovered point
                        const point = chart.series[0]?.searchPoint(event, true);

                        if (point) {
                            point.highlight(e);
                        }
                    }
                }
            });
        });
    }

    ngOnInit() {
        const subscribeTo = this.stationId ? { stationIds: [this.stationId] } : { locationIds: [this.locationUid] };
        this.notifications.connected$
            .pipe(
                filter((connected) => connected),
                untilDestroyed(this),
                take(1)
            )
            .subscribe(() => {
                this.notifications.subscribeUntil(subscribeTo, this.onDestroy$);
            });
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (
            (this.stationId || this.locationUid) &&
            this.connectors &&
            (changes.locationUid ||
                changes.stationId ||
                changes.connectors?.currentValue?.length !== changes.connectors?.previousValue?.length ||
                changes.startTime ||
                changes.endTime)
        ) {
            this.requestUpdate$.next();
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next();
    }

    private getFixedInterval() {
        let fixed_interval = '1h';
        if (this.zoomLevel === 'DAY') {
            fixed_interval = '10m';
        } else if (this.zoomLevel === 'HOUR') {
            fixed_interval = '2m';
        } else if (this.zoomLevel === 'CUSTOM') {
            const HOUR_IN_MINUTE = 1 * 60;
            const DAY_IN_MINUTE = HOUR_IN_MINUTE * 24;
            const { duration } = calculateDurationAndZoomLevel(this.startTime, this.endTime);

            if (duration <= 30) {
                fixed_interval = '5s';
            } else if (duration <= 2 * HOUR_IN_MINUTE) {
                fixed_interval = '1m';
            } else if (duration > HOUR_IN_MINUTE && duration <= DAY_IN_MINUTE) {
                fixed_interval = '5m';
            }
        }
        return fixed_interval;
    }

    /**
     * Fetch timestamps when charger has rebooted
     */
    async fetchBootTimes(): Promise<string[]> {
        const res = await this.reportingQuery.query(
            {
                query: {
                    size: 0,
                    indexName: 'schema-systemmetricshistory',
                    parameters: [
                        { term: { stationId: this.stationId } },
                        { terms: { type: ['CABINET', 'STANDALONE', 'TROLLEY'] } },
                        { range: { timestamp: { gte: this.startTime, lte: this.endTime } } },
                    ],
                    aggregations: {
                        bootTimes: {
                            terms: {
                                field: 'metrics.bootTime',
                                size: 50,
                            },
                        },
                    },
                },
            },
            { src: 'boot-times' }
        );

        const timestamps = Object.keys(res.response.bootTimes);
        const b = timestamps.map((t) => new Date(+t).toISOString());

        return b;
    }

    async fetchLocationPowerGraph() {
        return await this.reportingApi
            .getLocationPowerGraph(this.locationUid, {
                startTime: this.startTime,
                endTime: this.endTime,
                interval: this.getFixedInterval(),
            })
            .then((d) => d.data);
    }

    async fetchStationPowerGraph() {
        const totalPowerSamples = await this.reportingApi
            .getStationPowerGraph(this.stationId, {
                startTime: this.startTime,
                endTime: this.endTime,
                interval: this.getFixedInterval(),
            })
            .then((d) => d.data);
        return { totalPowerSamples, reservationSamples: [] };
    }

    async fetchPowerGraph(
        powerPerPmc: number,
        includeReservationSamples: boolean
    ): Promise<{ totalPowerSamples: [number, number, number][]; reservationSamples: [number, number, number][] }> {
        const fixed_interval = this.getFixedInterval();

        // get data points for the past and future for delta= 30 mins from the seekTime
        // elastic search points are updated every 15~20 mins
        const delta = 30 * 60 * 1000;
        const startTimeDelta = new Date(this.startTime).getTime() - delta;
        const endTimeDelta = new Date(this.endTime).getTime() + delta;

        const res = await this.reportingQuery.query(
            {
                query: {
                    indexName: 'schema-systemmetricshistory',
                    size: 0,
                    parameters: [
                        { term: { stationId: this.stationId } },
                        { term: { schema: 'systemMetricsHistory' } },
                        { terms: { 'metrics.type': ['CABINET', 'TROLLEY'] } },
                        {
                            range: {
                                timestamp: {
                                    gte: new Date(startTimeDelta).toISOString(),
                                    lte: new Date(endTimeDelta).toISOString(),
                                },
                            },
                        },
                    ],
                    disableEsParser: true,
                    aggregations: {
                        histogram: {
                            date_histogram: {
                                field: 'timestamp',
                                fixed_interval,
                            },
                            aggs: {
                                totalMaxPower: {
                                    max: {
                                        field: 'metrics.powerStats.max',
                                    },
                                },
                                ...(includeReservationSamples
                                    ? {
                                          pmcChargingCount: {
                                              max: {
                                                  field: 'additionalMetrics.pmcChargingCount',
                                              },
                                          },
                                      }
                                    : {}),
                            },
                        },
                    },
                },
            },
            { src: 'station-powergraph' }
        );

        return {
            totalPowerSamples: res.response.histogram.buckets
                .filter((b) => b.totalMaxPower?.value !== null)
                .map((b) => [b.key, b.totalMaxPower.value / 1000]),
            reservationSamples: !includeReservationSamples
                ? null
                : res.response.histogram.buckets
                      .filter((b) => b.pmcChargingCount?.value !== null)
                      .map((b) => [b.key, b.pmcChargingCount.value * powerPerPmc])
                      .reduce((acc, p, idx, ar) => {
                          const previous = ar[idx - 1];
                          if (previous) {
                              acc.push([p[0], previous[1]]);
                          }
                          acc.push(p);
                          return acc;
                      }, []),
        };
    }

    async fetchConnectorStatusHistory() {
        const res = await Promise.all(
            this.connectors?.map((connector) => {
                return this.statusApi.getEvseStatusHistory(this.stationId, connector.evseId, {
                    startTime: this.startTime,
                    endTime: this.endTime,
                });
            })
        );

        return res;
    }

    async fetchConnectionStatus() {
        const offlineThresholdMinutes = 0.5;
        const dayInMs = 24 * 60 * 60 * 1000;
        const startTime = new Date(Date.parse(this.startTime) - dayInMs).toISOString();
        const endTime = new Date(Date.parse(this.endTime) + dayInMs).toISOString();

        const res = await this.connectionsApi.getConnections(this.stationId, {
            consolidate: true,
            offlineThresholdMs: offlineThresholdMinutes * 60 * 1000,
            startTime,
            endTime,
            includeRearPastConnections: true,
        });
        return res;
    }

    openTxDetails(tx: Transaction) {
        this.dialogLink.open(['tx', tx.txId]);
    }

    openConnectorDetails(connector: Evse) {
        this.dialogLink.open(['connector', connector.stationId, connector.evseId]);
    }

    seekTimeChangeHandler(seekTime: string) {
        this.cdRef.markForCheck();
        this.seekTime = seekTime;
        this.seekTimeChange.emit(seekTime);
    }

    startTimeChangeHandler(startTime: string) {
        this.startTime = startTime;
        this.startTimeChange.emit(startTime);
        this.cdRef.markForCheck();
    }

    endTimeChangeHandler(endTime: string) {
        this.endTime = endTime;
        this.endTimeChange.emit(endTime);
        this.cdRef.markForCheck();
    }

    zoomLevelChangeHandler(zoomLevel: TimelineZoomLevel) {
        this.zoomLevel = zoomLevel;
        this.zoomLevelChange.emit(zoomLevel);
        this.cdRef.markForCheck();
    }
}
